if (!customElements.get('slite-interactive-colors')) {
  customElements.define('slite-interactive-colors', class SliteInteractiveColors extends HTMLElement {

    constructor() {
      super();
      this.phoneColorPicker = null;
      this.mainColor = this.dataset.mainColor || "rgb(255, 0, 0)";
      this.animateClockwise = this.dataset.animateClockwise || false;
      this.elements = {
        brightnessRange: this.querySelector('[data-slic-brightness-range]'),
        saturationRange: this.querySelector('[data-slic-saturation-range]'),
        colorNumber: this.querySelector('[data-slic-color-number]'),
        colorBrightness: this.querySelector('[data-slic-color-brightness]'),
        colorBrightnessList: this.querySelectorAll('[data-slic-color-brightness]'),
        colorSaturationList: this.querySelectorAll('[data-slic-color-saturation]'),
        ledPickerHandle: this.querySelector('[data-slic-led-picker-arrow]'),
        phoneColorPickerContainer: this.querySelector("[data-slic-phone-picker]"),
        phoneColorPickerHandle: this.querySelector('[data-slic-phone-picker-handle]'),
        iphoneDevice: this.querySelector('[data-device="iphone"]'),
        samsungDevice: this.querySelector('[data-device="samsung"]'),
      }
      
      this.style.setProperty('--iro-color-value', this.mainColor);

      if (this.iOS()) {
        this.elements.samsungDevice?.remove();
      } else {
        this.elements.iphoneDevice?.remove();
      }
      this.classList.add('ready');
      this.initializeColorPickers();
      this.addEventListeners();
      this.resizePhoneColorPicker();
    }

    iOS() {
      return /Macintosh|iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    }

    initializeColorPickers() {
      // const mainColor = new iro.Color(this.mainColor);
      // this.mainColorHue =  mainColor.hsv.h;
      // const initialHue = this.getHue(this.getHueProgress, this.mainColorHue);
      // Create a new color picker instance
      // https://iro.js.org/guide.html#Getting-Started
      this.phoneColorPicker = new iro.ColorPicker(this.elements.phoneColorPickerContainer, {
        // color picker options
        // Option guide: https://iro.js.org/guide.html#Color-Picker-Options
        width: this.elements.phoneColorPickerContainer?.clientWidth || 260,
        display: 'block',
        color: this.mainColor,
        borderWidth: 0,
        borderColor: "#1D1D1D",
        padding: 0,
        margin: 0,
        wheelLightness: false,
        layout: [{
          component: iro.ui.Wheel
        }]
      });
      this.mainColorHue = this.phoneColorPicker.color.hsv.h;
      this.onScroll();
    }

    swapClasses(element, activeClass) {
      const otherClass = activeClass === 'black' ? 'white' : 'black';
      element.classList.add(activeClass);
      element.classList.remove(otherClass);
    }

    addEventListeners() {
      const intersectionCallback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            document.body.classList.add('interactive-colors');
          } else {
            document.body.classList.remove('interactive-colors');
          }
        });
      };
      const observer = new IntersectionObserver(intersectionCallback, {
        root: null,
        rootMargin: '0px',
        threshold: 0
      });
      observer.observe(this);
      this.elements.brightnessRange.addEventListener('input', function (event) {
        requestAnimationFrame(function() {
          this.phoneColorPicker.color.hsv = {
            h: this.phoneColorPicker.color.hsl.h,
            s: this.elements.saturationRange.value,
            v: this.elements.brightnessRange.value
          };
        
          this.updateSpotColor(this.phoneColorPicker.color);
        }.bind(this));
      }.bind(this));
      
      this.elements.saturationRange.addEventListener('input', function (event) {
        requestAnimationFrame(function() {
          this.phoneColorPicker.color.hsv = {
            h: this.phoneColorPicker.color.hsl.h,
            s: this.elements.saturationRange.value,
            v: this.elements.brightnessRange.value
          };
          this.updateSpotColor(this.phoneColorPicker.color);
        }.bind(this))
      }.bind(this));
      this.phoneColorPicker.on(['mount','input:move'], function() {
        requestAnimationFrame(function() {
          this.updatePickerHandlerPosition()
        }.bind(this));
      }.bind(this));
      this.phoneColorPicker.on(['color:init', 'color:change'], function (color) {
        requestAnimationFrame(function() {
          this.updateSpotColor(color);
          this.updateBrightness(`${color.hsv.v}%`);
          this.updateSaturation(`${color.hsv.s}%`);
          this.elements.colorNumber.innerHTML = `${color.hsv.h}`;
          this.elements.colorBrightness.innerHTML = `${color.hsv.v}%`;
          this.elements.saturationRange.value = color.hsv.s;
          this.style.setProperty('--iro-color-bg-value', color.rgbString);
          document.body.style.setProperty('--iro-color-bg-value', color.rgbString);
          this.swapClasses(this,this.getContrastColorClass(this.phoneColorPicker.color));
        }.bind(this));
      }.bind(this));

      window.addEventListener('resize', function() {
        requestAnimationFrame(function() {
          this.resizePhoneColorPicker();
          this.updatePickerHandlerPosition();
        }.bind(this));
      }.bind(this));

      window.addEventListener('scroll', function() {
        this.onScroll();
      }.bind(this));
    }

    updatePickerHandlerPosition() {
      const pickerHandle = this.phoneColorPicker.el.querySelector('.IroHandle');
      const position = this.getTranslateXY(pickerHandle);
      this.elements.phoneColorPickerHandle.style.transform = `translateX(${position.translateX - (this.elements.phoneColorPickerHandle.offsetWidth /2)}px) translateY(${position.translateY - (this.elements.phoneColorPickerHandle.offsetHeight/2)}px)`;
    }

    getHue(progress, startingValue) {
      const newHue = startingValue + (0.5 * 360) + (this.animateClockwise ? 1 - progress : progress) * 360;
      return Math.round(Math.abs(newHue % 360));
    }

    getContrastColorClass(color) {
      const r = color.red;
      const g = color.green;
      const b = color.blue;
      const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    
      if (luminance > 0.5) {
        return 'black'; // return black for light backgrounds
      } else {
        return 'white'; // return white for dark backgrounds
      }
    }

    getHueProgress() {
      const scrollTop = this.getBoundingClientRect().top + document.documentElement.scrollTop;
      const startPoint = scrollTop - window.innerHeight;
      const maxScrollTop = scrollTop + this.offsetHeight;
      const endPoint = maxScrollTop - window.innerHeight;
      const range = maxScrollTop+this.getBoundingClientRect().top;
      const from = this.getBoundingClientRect().top - window.innerHeight
      if ( from < 0 && range >= scrollTop) {
        return (document.documentElement.scrollTop - startPoint) / (endPoint - startPoint + window.innerHeight);
      } else {
        return 0;
      }
    }

    onScroll() {
      const scrollTop = this.getBoundingClientRect().top + document.documentElement.scrollTop;
      const startPoint = scrollTop - window.innerHeight;
      const maxScrollTop = scrollTop + this.offsetHeight;
      const endPoint = maxScrollTop - window.innerHeight;
      const range = maxScrollTop+this.getBoundingClientRect().top;
      const from = this.getBoundingClientRect().top - window.innerHeight
      if ( from < 0 && range >= scrollTop) {
        let progress = (document.documentElement.scrollTop - startPoint) / (endPoint - startPoint);
        let hueProgress = (document.documentElement.scrollTop - startPoint) / (endPoint - startPoint + window.innerHeight);
        const brightnessStartProgress = (progress - 0.2) / 0.5;
        const brightnessEndProgress = (progress - 0.9) / 0.7;
        let brightnessProgress = 0;
        if ( brightnessStartProgress > 0) {
          brightnessProgress = 100;
          if (brightnessStartProgress < 1) {
            brightnessProgress = Math.ceil(100 * this.easeInOutCirc(brightnessStartProgress));
          }
        }
        if (brightnessEndProgress > 0) {
          brightnessProgress = 0;
          if (brightnessEndProgress < 1) {
            brightnessProgress = 100 - Math.ceil(100 * this.easeInOutCirc(brightnessEndProgress));
          }
        }
        if (progress > 1) {
          progress = 1;
        }
        this.phoneColorPicker.color.hsv = {
          h: this.getHue(hueProgress, this.mainColorHue),
          s: this.elements.saturationRange.value,
          v: brightnessProgress
        };
        this.elements.brightnessRange.value = brightnessProgress;
        this.elements.brightnessRange.dispatchEvent(new Event('input'));
        this.phoneColorPicker.emit('input:move');
      }
    }

    easeInCirc(x) {
      return 1 - Math.sqrt(1 - Math.pow(x, 2));
    }

    easeInOutCirc(x) {
      return .5*(Math.sin((x - .5)*Math.PI) + 1);
    }

    getTranslateXY(element) {
      const style = window.getComputedStyle(element)
      const matrix = new DOMMatrixReadOnly(style.transform)
      return {
          translateX: matrix.m41,
          translateY: matrix.m42
      }
  }

    resizePhoneColorPicker() {
      if (this.elements.phoneColorPickerContainer) {
        this.phoneColorPicker.resize(this.elements.phoneColorPickerContainer.clientWidth);
      }
    }

    updateBrightness(value) {
      [...this.elements.colorBrightnessList].forEach(colorBrightness => {
        colorBrightness.innerHTML = value;
      })
    }

    updateSaturation(value) {
      [...this.elements.colorSaturationList].forEach(colorSaturation => {
        colorSaturation.innerHTML = value;
      })
    }

    updateSpotColor = (color) => {
      const spotColor = iro.Color.hsvToRgb({
        h: color.hsl.h,
        s: `${color.hsv.s}`,
        v: "100"
      });
      this.elements.saturationRange.value = color.hsv.s;
      this.elements.saturationRange.style.setProperty('--value', `${color.hsv.s}`);
      this.phoneColorPicker.emit('input:move');
      this.style.setProperty('--iro-color-value', `rgb(${spotColor.r},${spotColor.g},${spotColor.b})`);
    }
  });
}